import { useMemo } from "react";
import { Loader2 } from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useDashboardChart } from "@/hooks/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { TableCell, TableRow } from "@/components/ui/table";
import {
  DashboardTable,
  useDashboardTable,
  type DashboardTableColumn,
} from "./dashboard-table";
import { useDashboardUrlState } from "./useDashboardUrlState";
import { FiltersCard } from "./filters-card";

const DASHBOARD_ID = "cohort-conversion";

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().split("T")[0]!;
}

const CHANNELS = ["", "Meta", "Google", "TikTok", "Organisk"] as const;
const BAR_COLOR = "#D0A84C";
const COUNT_COLOR = "var(--color-admin-info)";
const SIZE_COLOR = "var(--color-admin-text-subtle)";
const axisStyle = { fontSize: 11, fill: "rgba(244, 244, 245, 0.60)" };

type CohortRow = {
  cohort: string;
  channel: string;
  signups: number | string;
  converted: number | string;
  conv_pct: string;
  conv_mnd_0: number | string;
  conv_mnd_1: number | string;
  conv_mnd_2: number | string;
  conv_mnd_3_5: number | string;
  conv_mnd_6plus: number | string;
  avg_days: string | null;
};

// "Kanal" is not column-filtered: the channel chips in the page's filter
// card already drive the query for every card on this dashboard.
const COHORT_GRID_COLUMNS: DashboardTableColumn<CohortRow>[] = [
  { id: "cohort", label: "Kohort", value: (r) => r.cohort },
  { id: "channel", label: "Kanal", value: (r) => r.channel },
  { id: "signups", label: "Signups", value: (r) => r.signups, numeric: true, align: "right" },
  { id: "converted", label: "Konvertert", value: (r) => r.converted, numeric: true, align: "right" },
  {
    id: "conv_pct",
    label: "Konv. %",
    value: (r) => (r.conv_pct === "" ? null : Number(r.conv_pct)),
    numeric: true,
    align: "right",
  },
  { id: "conv_mnd_0", label: "Mnd 0", value: (r) => r.conv_mnd_0, numeric: true, align: "right" },
  { id: "conv_mnd_1", label: "Mnd 1", value: (r) => r.conv_mnd_1, numeric: true, align: "right" },
  { id: "conv_mnd_2", label: "Mnd 2", value: (r) => r.conv_mnd_2, numeric: true, align: "right" },
  { id: "conv_mnd_3_5", label: "Mnd 3–5", value: (r) => r.conv_mnd_3_5, numeric: true, align: "right" },
  { id: "conv_mnd_6plus", label: "Mnd 6+", value: (r) => r.conv_mnd_6plus, numeric: true, align: "right" },
  { id: "avg_days", label: "Snitt dager", value: (r) => r.avg_days, numeric: true, align: "right" },
  { id: "actions", label: "", sortable: false },
];

type KpiRow = {
  signups_count: number | string;
  converted_count: number | string;
  conversion_rate: string | null;
  median_days_to_convert: number | string | null;
  emails_total: number | string;
  emails_no_bankid: number | string;
  best_channel: string | null;
  best_channel_conv_pct: string | null;
  best_channel_signups: number | string | null;
};

type BucketRow = {
  bucket: string;
  count: number | string;
  sort_order: number | string;
};

type DemoRow = {
  label: string;
  signups: number | string;
  converted: number | string;
  conv_pct: string;
};

function BarTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md border border-admin-card-border bg-admin-card px-3 py-2 shadow-lg">
      <p className="text-xs font-medium text-admin-text-primary mb-1">{label}</p>
      <p className="text-xs" style={{ color: BAR_COLOR }}>
        {Number(payload[0]?.value).toLocaleString("no-NO")} brukere
      </p>
    </div>
  );
}

function DemoTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload as DemoRow | undefined;
  return (
    <div className="rounded-md border border-admin-card-border bg-admin-card px-3 py-2 shadow-lg">
      <p className="text-xs font-medium text-admin-text-primary mb-1">{label}</p>
      <p className="text-xs" style={{ color: BAR_COLOR }}>
        {d?.conv_pct ?? "—"}% konverteringsrate
      </p>
      <p className="text-xs text-admin-text-secondary">
        {String(d?.converted ?? 0)} av {String(d?.signups ?? 0)} signups konverterte
      </p>
    </div>
  );
}

export function CohortConversionDashboard() {
  const [{ from, to, channel, selectedCohort }, update] = useDashboardUrlState({
    from: isoDaysAgo(365),
    to: isoDaysAgo(0),
    channel: "",
    selectedCohort: "",
  });

  const gridParams = useMemo(
    () => ({ from, to, channel: channel || undefined }),
    [from, to, channel]
  );

  const histogramParams = useMemo(
    () => ({ from, to, cohort: selectedCohort || undefined, channel: channel || undefined }),
    [from, to, selectedCohort, channel]
  );

  const demographicParams = useMemo(
    () => ({ from, to, cohort: selectedCohort || undefined, channel: channel || undefined }),
    [from, to, selectedCohort, channel]
  );

  const kpis = useDashboardChart<typeof gridParams, KpiRow>(
    DASHBOARD_ID,
    "kpis",
    gridParams
  );

  const cohortGrid = useDashboardChart<typeof gridParams, CohortRow>(
    DASHBOARD_ID,
    "cohort-grid",
    gridParams
  );

  const timeToConvert = useDashboardChart<typeof histogramParams, BucketRow>(
    DASHBOARD_ID,
    "time-to-convert",
    histogramParams
  );

  const demographicGender = useDashboardChart<typeof demographicParams, DemoRow>(
    DASHBOARD_ID,
    "demographic-gender",
    demographicParams
  );

  const demographicAge = useDashboardChart<typeof demographicParams, DemoRow>(
    DASHBOARD_ID,
    "demographic-age",
    demographicParams
  );

  const availableCohorts = useMemo(() => {
    if (!cohortGrid.data) return [];
    return [...new Set(cohortGrid.data.map((r) => r.cohort))].sort().reverse();
  }, [cohortGrid.data]);

  const displayedGridRows = useMemo(() => {
    if (!cohortGrid.data) return [];
    if (!selectedCohort) return cohortGrid.data;
    return cohortGrid.data.filter((r) => r.cohort === selectedCohort);
  }, [cohortGrid.data, selectedCohort]);

  const cohortGridTable = useDashboardTable(
    "cohort-grid",
    COHORT_GRID_COLUMNS,
    displayedGridRows,
  );

  return (
    <div className="space-y-6">
      <FiltersCard>
        <div className="flex flex-wrap items-end gap-3">
          <label className="space-y-1">
            <span className="text-xs text-admin-text-subtle">Fra</span>
            <Input
              type="date"
              value={from}
              onChange={(e) => update({ from: e.target.value })}
              className="w-44"
            />
          </label>
          <label className="space-y-1">
            <span className="text-xs text-admin-text-subtle">Til</span>
            <Input
              type="date"
              value={to}
              onChange={(e) => update({ to: e.target.value })}
              className="w-44"
            />
          </label>
        </div>
        <div className="flex flex-wrap gap-2">
          {CHANNELS.map((ch) => (
            <Button
              key={ch || "all"}
              variant={channel === ch ? "default" : "outline"}
              size="sm"
              onClick={() => update({ channel: ch })}
            >
              {ch === "" ? "Alle kanaler" : ch}
            </Button>
          ))}
        </div>
        {availableCohorts.length > 0 && (
          <div className="flex flex-wrap gap-2">
            <Button
              variant={selectedCohort === "" ? "default" : "outline"}
              size="sm"
              onClick={() => update({ selectedCohort: "" })}
            >
              Alle kohorter
            </Button>
            {availableCohorts.map((c) => (
              <Button
                key={c}
                variant={selectedCohort === c ? "default" : "outline"}
                size="sm"
                onClick={() => update({ selectedCohort: selectedCohort === c ? "" : c })}
              >
                {c}
              </Button>
            ))}
          </div>
        )}
      </FiltersCard>

      <KpiRow row={kpis.data?.[0]} isLoading={kpis.isLoading} />

      <Card>
        <CardHeader>
          <CardTitle>Kohortgrid</CardTitle>
          <p className="text-xs text-admin-text-subtle mt-1 max-w-3xl leading-relaxed">
            Hver rad er en gruppe brukere som logget seg inn med BankID i den
            måneden. <span className="font-medium">Signups</span> = antall
            brukere i kohorten. <span className="font-medium">Konvertert</span>{" "}
            = hvor mange av dem som senere fikk et abonnement (aktivt,
            stoppet eller utløpt — vi teller alle som noen gang har betalt).
            Mnd-kolonnene viser når de konverterte: <em>Mnd 0</em> = samme
            måned som BankID, <em>Mnd 1</em> = måneden etter, osv. Brukere
            som har et eksisterende abonnement før BankID (B2B, gammel
            webcheckout) regnes som <em>Mnd 0</em>.
          </p>
          <p className="text-xs text-admin-text-subtle mt-2 max-w-3xl leading-relaxed">
            <span className="font-medium">Kanal</span> kommer fra ad-attribusjon
            (fbclid/gclid/ttclid) i onboarding-skjemaet, koblet via e-post. Brukere
            uten match får <em>Organisk</em>.
          </p>
        </CardHeader>
        <CardContent>
          {cohortGrid.isLoading ? (
            <ChartSkeleton />
          ) : cohortGrid.isError ? (
            <ErrorBlock message={cohortGrid.error?.message} />
          ) : displayedGridRows.length === 0 ? (
            <EmptyBlock>Ingen data i valgt periode.</EmptyBlock>
          ) : (
            <DashboardTable
              table={cohortGridTable}
              columns={COHORT_GRID_COLUMNS}
              renderRow={(row, i) => (
                <TableRow key={`${row.cohort}-${row.channel}-${i}`}>
                  <TableCell className="font-mono text-admin-text-primary">
                    {row.cohort}
                  </TableCell>
                  <TableCell className="text-admin-text-secondary">
                    {row.channel}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {String(row.signups)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {String(row.converted)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums font-medium">
                    {row.conv_pct ?? "—"}%
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-admin-text-secondary">
                    {String(row.conv_mnd_0)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-admin-text-secondary">
                    {String(row.conv_mnd_1)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-admin-text-secondary">
                    {String(row.conv_mnd_2)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-admin-text-secondary">
                    {String(row.conv_mnd_3_5)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-admin-text-secondary">
                    {String(row.conv_mnd_6plus)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-admin-text-subtle">
                    {row.avg_days ?? "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant={selectedCohort === row.cohort ? "default" : "outline"}
                      size="sm"
                      onClick={() =>
                        update({
                          selectedCohort:
                            selectedCohort === row.cohort ? "" : row.cohort,
                        })
                      }
                    >
                      Histogram
                    </Button>
                  </TableCell>
                </TableRow>
              )}
            />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            Tid til konvertering
            {selectedCohort && (
              <span className="ml-2 font-mono text-admin-primary text-sm font-normal">
                {selectedCohort}
              </span>
            )}
          </CardTitle>
          <p className="text-xs text-admin-text-subtle mt-1 max-w-3xl leading-relaxed">
            Fordeling av dager fra BankID-innlogging til første abonnement.
            Viser bare brukere som faktisk har konvertert. Brukere som hadde
            et abonnement før BankID (B2B, gammel webcheckout) regnes som
            samme-dag-konvertering (0–7 dager).
          </p>
        </CardHeader>
        <CardContent>
          {timeToConvert.isLoading ? (
            <ChartSkeleton />
          ) : timeToConvert.isError ? (
            <ErrorBlock message={timeToConvert.error?.message} />
          ) : !timeToConvert.data || timeToConvert.data.length === 0 ? (
            <EmptyBlock>Ingen konverteringer i valgt periode.</EmptyBlock>
          ) : (
            <div className="rounded-md border border-admin-card-border bg-admin-card p-4">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart
                  data={timeToConvert.data.map((r) => ({
                    bucket: r.bucket,
                    count: Number(r.count),
                  }))}
                  margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#2B2B31" />
                  <XAxis dataKey="bucket" tick={axisStyle} />
                  <YAxis
                    tick={axisStyle}
                    allowDecimals={false}
                    width={36}
                  />
                  <Tooltip content={<BarTooltip />} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                  <Bar dataKey="count" name="Brukere" fill={BAR_COLOR} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <DemoChart title="Kjønn" query={demographicGender} />
        <DemoChart title="Aldersgruppe" query={demographicAge} />
      </div>
    </div>
  );
}

function DemoChart({
  title,
  query,
}: {
  title: string;
  query: ReturnType<typeof useDashboardChart<{ from: string; to: string }, DemoRow>>;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {query.isLoading ? (
          <ChartSkeleton />
        ) : query.isError ? (
          <ErrorBlock message={query.error?.message} />
        ) : !query.data || query.data.length === 0 ? (
          <EmptyBlock>Ingen data.</EmptyBlock>
        ) : (
          <div className="rounded-md border border-admin-card-border bg-admin-card p-4">
            <ResponsiveContainer width="100%" height={220}>
              <ComposedChart
                data={query.data.map((r) => ({
                  ...r,
                  conv_pct_num: Number(r.conv_pct),
                  signups_num: Number(r.signups),
                  converted_num: Number(r.converted),
                }))}
                margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#2B2B31" />
                <XAxis dataKey="label" tick={axisStyle} />
                <YAxis
                  yAxisId="pct"
                  orientation="left"
                  tick={axisStyle}
                  tickFormatter={(v: number) => `${v}%`}
                  domain={[0, 100]}
                  stroke={BAR_COLOR}
                  width={40}
                />
                <YAxis
                  yAxisId="count"
                  orientation="right"
                  tick={axisStyle}
                  allowDecimals={false}
                  stroke={COUNT_COLOR}
                  width={40}
                />
                <Tooltip content={<DemoTooltip />} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar yAxisId="pct" dataKey="conv_pct_num" name="Konv. %" fill={BAR_COLOR} radius={[3, 3, 0, 0]} />
                <Bar
                  yAxisId="count"
                  dataKey="signups_num"
                  name="Signups (antall)"
                  fill={SIZE_COLOR}
                  radius={[3, 3, 0, 0]}
                />
                <Bar
                  yAxisId="count"
                  dataKey="converted_num"
                  name="Konvertert (antall)"
                  fill={COUNT_COLOR}
                  radius={[3, 3, 0, 0]}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ChartSkeleton() {
  return (
    <div className="flex items-center gap-2 text-admin-text-subtle text-sm py-8 justify-center">
      <Loader2 className="h-4 w-4 animate-spin" />
      Laster…
    </div>
  );
}

function EmptyBlock({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-sm text-admin-text-subtle py-6 text-center">{children}</p>
  );
}

function ErrorBlock({ message }: { message?: string }) {
  return (
    <div className="rounded-md border border-admin-danger-500/30 bg-admin-danger-500/5 p-4 text-sm text-admin-danger-400">
      {message || "Ukjent feil"}
    </div>
  );
}

function KpiRow({
  row,
  isLoading,
}: {
  row: KpiRow | undefined;
  isLoading: boolean;
}) {
  // Conversion rate
  const convRate = row?.conversion_rate ?? null;
  const converted = row ? Number(row.converted_count) : 0;
  const signups = row ? Number(row.signups_count) : 0;

  // Median days
  const median = row?.median_days_to_convert;
  const medianValue = median === null || median === undefined ? null : Number(median);

  // Emails without BankID
  const emailsTotal = row ? Number(row.emails_total) : 0;
  const emailsNoBankId = row ? Number(row.emails_no_bankid) : 0;
  const emailsNoBankIdPct =
    emailsTotal > 0
      ? ((emailsNoBankId / emailsTotal) * 100).toFixed(1)
      : null;

  // Best channel
  const bestChannel = row?.best_channel ?? null;
  const bestChannelPct = row?.best_channel_conv_pct ?? null;
  const bestChannelSignups = row?.best_channel_signups ?? null;

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <KpiTile
        label="Konverteringsrate"
        value={isLoading ? "…" : convRate ? `${convRate}%` : "—"}
        help={
          isLoading
            ? " "
            : signups > 0
              ? `${converted} av ${signups} signups`
              : "ingen signups i perioden"
        }
      />
      <KpiTile
        label="Median dager til konvertering"
        value={
          isLoading
            ? "…"
            : medianValue !== null
              ? `${medianValue} ${medianValue === 1 ? "dag" : "dager"}`
              : "—"
        }
        help={
          isLoading
            ? " "
            : medianValue !== null
              ? `halvparten innen ${medianValue} ${medianValue === 1 ? "dag" : "dager"}`
              : "ingen konverteringer"
        }
      />
      <KpiTile
        label="E-post uten BankID"
        value={isLoading ? "…" : emailsTotal > 0 ? String(emailsNoBankId) : "—"}
        help={
          isLoading
            ? " "
            : emailsTotal > 0
              ? `${emailsNoBankIdPct}% av ${emailsTotal} e-poster på landingsside`
              : "ingen e-poster i perioden"
        }
      />
      <KpiTile
        label="Beste kanal"
        value={
          isLoading
            ? "…"
            : bestChannel
              ? `${bestChannel} @ ${bestChannelPct}%`
              : "—"
        }
        help={
          isLoading
            ? " "
            : bestChannel
              ? `${bestChannelSignups} signups`
              : "krever ≥ 10 signups per kanal"
        }
      />
    </div>
  );
}

function KpiTile({
  label,
  value,
  help,
}: {
  label: string;
  value: string;
  help: string;
}) {
  return (
    <div className="rounded-md border border-admin-card-border bg-admin-card p-4">
      <p className="text-xs text-admin-text-subtle">{label}</p>
      <p className="text-2xl font-semibold tabular-nums text-admin-text-primary mt-1">
        {value}
      </p>
      <p className="text-xs text-admin-text-subtle mt-2 leading-snug">{help}</p>
    </div>
  );
}
